import { useMemo } from 'react';
import activationData from '../activation-data.json';
import { codeToCountryName } from '../utils/completeCountryMapping';

const DataTable = ({ region, data, ipType = 'ipv4' }) => {
    // Process data for the table
    const tableData = useMemo(() => {
        if (!data || !data[region]) return [];

        const regionData = data[region];
        const countryRows = [];

        // Function to calculate difference in days between dates
        const calculateDaysDifference = (activationDateStr, firstGeoipDateStr) => {
            if (!activationDateStr || !firstGeoipDateStr) return null;
            const activationDate = new Date(activationDateStr);
            const firstGeoipDate = new Date(firstGeoipDateStr);
            const diffTime = activationDate - firstGeoipDate; // Positive if activation is after  IP geolocation, negative if before
            const diffDays = Math.ceil(Math.abs(diffTime) / (1000 * 60 * 60 * 24));
            return diffTime < 0 ? -diffDays : diffDays; // Negative when activation comes before  IP geolocation
        };

        // Function to convert timestamp to UTC date
        const timestampToDate = (timestamp) => {
            const date = new Date(timestamp * 1000);
            return date.toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                timeZone: 'UTC'
            });
        };

        Object.entries(regionData).forEach(([countryCode, countryData]) => {
            const ipData = countryData[ipType];
            if (ipData) {
                const timestamps = Object.keys(ipData)
                    .map(t => parseInt(t))
                    .sort((a, b) => a - b);

                // Find the first date where the value is not zero
                let firstNonZeroTimestamp = null;
                let firstGeoipEntry = 'Already Present';

                for (const timestamp of timestamps) {
                    const value = parseFloat(ipData[timestamp]) || 0;
                    if (value > 0) {
                        firstNonZeroTimestamp = timestamp;
                        break;
                    }
                }

                // If we found a date with value > 0, check if it's the first date
                if (firstNonZeroTimestamp) {
                    const firstTimestamp = timestamps[0];
                    const firstValue = parseFloat(countryData[ipType][firstTimestamp]) || 0;

                    if (firstValue === 0) {
                        // The first date was zero, show the date where it stopped being zero
                        firstGeoipEntry = timestampToDate(firstNonZeroTimestamp);
                    }
                    // Si firstValue > 0, mantener "Already Present"
                }

                // Get announced activation date and link
                const activationInfo = activationData[countryCode] || { date: null, link: null };
                const activationDate = activationInfo.date;
                const activationLink = activationInfo.link;
                let activationAnnounced = activationDate ? activationDate : 'Not Officially Available Yet';

                // Calculate time range
                let timeRange = 'N/A';
                if (activationDate && firstGeoipEntry !== 'Already Present') {
                    const daysDiff = calculateDaysDifference(activationDate, firstGeoipEntry);
                    if (daysDiff !== null) {
                        if (daysDiff < 0) {
                            timeRange = `${daysDiff} days`; // Will show negative values like "-72 days"
                        } else {
                            timeRange = `${daysDiff} days`;
                        }
                    }
                }

                countryRows.push({
                    code: countryCode,
                    name: codeToCountryName[countryCode] || countryCode,
                    firstGeoipEntry,
                    activationAnnounced,
                    activationLink,
                    timeRange
                });
            }
        });

        // Sort by country name
        return countryRows.sort((a, b) => a.name.localeCompare(b.name));
    }, [data, region, ipType]);

    if (tableData.length === 0) {
        return (
            <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#666',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
            }}>
                <p>No data available for this region and IP type.</p>
            </div>
        );
    }

    return (
        <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
        }}>
            <div style={{
                padding: '15px 20px',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #e2e8f0',
                fontWeight: '600',
                color: '#2d3748'
            }}>
                {region} - {ipType.toUpperCase()} Data Summary ({tableData.length} countries)
            </div>

            <div style={{
                fontSize: '0.85rem'
            }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{
                        backgroundColor: '#edf2f7'
                    }}>
                        <tr>
                            <th style={{
                                padding: '12px 8px',
                                textAlign: 'left',
                                borderBottom: '2px solid #cbd5e0',
                                fontWeight: '600',
                                color: '#4a5568',
                                fontSize: '0.8rem'
                            }}>Country</th>
                            <th style={{
                                padding: '12px 8px',
                                textAlign: 'center',
                                borderBottom: '2px solid #cbd5e0',
                                fontWeight: '600',
                                color: '#4a5568',
                                fontSize: '0.8rem'
                            }}>First  IP geolocation Entry</th>
                            <th style={{
                                padding: '12px 8px',
                                textAlign: 'center',
                                borderBottom: '2px solid #cbd5e0',
                                fontWeight: '600',
                                color: '#4a5568',
                                fontSize: '0.8rem'
                            }}>Activation Announced</th>
                            <th style={{
                                padding: '12px 8px',
                                textAlign: 'center',
                                borderBottom: '2px solid #cbd5e0',
                                fontWeight: '600',
                                color: '#4a5568',
                                fontSize: '0.8rem'
                            }}>Time Range</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map((row, index) => (
                            <tr key={row.code} style={{
                                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                borderBottom: '1px solid #e2e8f0'
                            }}>
                                <td style={{ padding: '10px 8px' }}>
                                    <div>
                                        <div style={{ fontWeight: '600', color: '#2d3748' }}>
                                            {row.name}
                                        </div>
                                        <div style={{ fontSize: '0.7rem', color: '#718096', marginTop: '2px' }}>
                                            {row.code}
                                        </div>
                                    </div>
                                </td>
                                <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                    <div style={{
                                        color: row.firstGeoipEntry === 'Already Present' ? '#01B4BD' : '#4a5568',
                                        fontWeight: row.firstGeoipEntry === 'Already Present' ? '600' : 'normal',
                                        fontSize: '0.85rem'
                                    }}>
                                        {row.firstGeoipEntry}
                                    </div>
                                </td>
                                <td style={{
                                    padding: '10px 8px',
                                    textAlign: 'center',
                                    fontSize: '0.85rem'
                                }}>
                                    <div style={{
                                        color: row.activationAnnounced === 'Not Officially Available Yet' ? '#FA541C' : '#4a5568',
                                        fontWeight: row.activationAnnounced === 'Not Officially Available Yet' ? '500' : 'normal'
                                    }}>
                                        {row.activationLink && row.activationAnnounced !== 'Not Officially Available Yet' ? (
                                            <a
                                                href={row.activationLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    color: '#096DD9',
                                                    textDecoration: 'none',
                                                    borderBottom: '1px solid #096DD9',
                                                    paddingBottom: '1px'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.color = '#0958A3';
                                                    e.target.style.borderBottomColor = '#0958A3';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.color = '#096DD9';
                                                    e.target.style.borderBottomColor = '#096DD9';
                                                }}
                                            >
                                                {row.activationAnnounced}
                                            </a>
                                        ) : (
                                            row.activationAnnounced
                                        )}
                                    </div>
                                </td>
                                <td style={{
                                    padding: '10px 8px',
                                    textAlign: 'center',
                                    fontSize: '0.85rem'
                                }}>
                                    <div style={{
                                        color: row.timeRange === 'N/A' ? '#01B4BD' :
                                            row.timeRange.includes('-') ? '#d69e2e' : '#4a5568', // Orange for negative values
                                        fontWeight: row.timeRange.includes('-') ? '600' : 'normal'
                                    }}>
                                        {row.timeRange}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DataTable;
